"use client";

import { cn } from "@frontend/shared/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

const KioskDialog = DialogPrimitive.Root;

const KioskDialogTrigger = DialogPrimitive.Trigger;

const KioskDialogPortal = DialogPrimitive.Portal;

const KioskDialogClose = DialogPrimitive.Close;

interface KioskDialogOverlayProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> {
  "data-devtool"?: boolean;
}

const KioskDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  KioskDialogOverlayProps
>(({ className, "data-devtool": dataDevtool, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-devtool={dataDevtool}
    className={cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
KioskDialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface KioskDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  "data-devtool"?: boolean;
}

const KioskDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  KioskDialogContentProps
>(({ className, children, "data-devtool": dataDevtool, ...props }, ref) => (
  <KioskDialogPortal>
    <KioskDialogOverlay data-devtool={dataDevtool} />
    <DialogPrimitive.Content
      ref={ref}
      data-devtool={dataDevtool}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-6",
        "bg-white rounded-2xl p-6 md:rounded-3xl md:p-12 shadow-2xl",
        "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
        "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        data-devtool={dataDevtool}
        className={cn(
          "absolute right-6 top-6 rounded-full p-2",
          "text-gray-400 hover:text-gray-600 hover:bg-gray-100",
          "transition-colors duration-200",
          "focus:outline-none focus:ring-2 focus:ring-kioskPrimary focus:ring-offset-2",
          "disabled:pointer-events-none",
        )}
      >
        <X className="h-5 w-5" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </KioskDialogPortal>
));
KioskDialogContent.displayName = DialogPrimitive.Content.displayName;

const KioskDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-4 text-center", className)}
    {...props}
  />
);
KioskDialogHeader.displayName = "KioskDialogHeader";

const KioskDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row gap-6", className)}
    {...props}
  />
);
KioskDialogFooter.displayName = "KioskDialogFooter";

const KioskDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      " text-gray-900 leading-tight font-suisse font-semibold",
      className,
    )}
    {...props}
  />
));
KioskDialogTitle.displayName = DialogPrimitive.Title.displayName;

const KioskDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-base text-gray-600 leading-5", className)}
    {...props}
  />
));
KioskDialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  KioskDialog,
  KioskDialogPortal,
  KioskDialogOverlay,
  KioskDialogClose,
  KioskDialogTrigger,
  KioskDialogContent,
  KioskDialogHeader,
  KioskDialogFooter,
  KioskDialogTitle,
  KioskDialogDescription,
};
